import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import type { AuthContextType } from 'contexts';
import { UserRoles } from '@bb/users';
import { IListing } from '@bb/domain/dist/types';
import { UNVERIFIED_AGENT_MODAL } from '@app/constants';
import { message, Modal } from 'components';
import { FavoriteController } from 'api';
import { HashRouter, Route, useHistory } from 'react-router-dom';
import { featureEnabled } from 'utils';
import { RegisterOfferButton } from './RegisterOfferButton';
import { BookShowingButton } from './BookShowingButton';
import { DropdownBookingActions } from './DropdownBookingActions';
import { DropdownQuickListingActions } from './DropdownQuickListingActions';

type ListingActionButtonsType = {
  listing: IListing & { id: string };
  isAutoConfirm: boolean;
  isCoopAgent: boolean;
  isDisabledBtns: boolean;
  showAddToTour: boolean;
  showBookShowingBtn: boolean;
  showRegisterOfferBtn: boolean;
  showQuickListingActionButtons: boolean;
  $rootScope: {
    auth: AuthContextType['auth'];
  };
  preBookingInformation: string | undefined;
  hasFeature: AuthContextType['hasFeature'];
};

const ListingActionButtons = ({
  listing,
  isAutoConfirm,
  isCoopAgent,
  isDisabledBtns,
  showAddToTour,
  showBookShowingBtn,
  showRegisterOfferBtn,
  showQuickListingActionButtons,
  $rootScope: { auth },
  preBookingInformation,
  hasFeature,
}: ListingActionButtonsType) => {
  const history = useHistory();
  const [userFavorites, setUserFavorites] = useState<Set<string>>(new Set());

  const isFavorite = useMemo(
    () => userFavorites && userFavorites.has(listing.id),
    [userFavorites, listing.id],
  );

  const isListingAgent = useMemo(
    () => listing.reps.filter(repId => repId === auth.id).length > 0,
    [listing.reps],
  );

  const isAgent = useMemo(() => auth.role === UserRoles.Agent, [auth.role]);

  const isUnverified = useMemo(
    () => Boolean(auth.isUnverified),
    [auth.isUnverified],
  );

  const bookWarningModal = useCallback(() => {
    if (isAgent && !isListingAgent && isUnverified) {
      Modal.warning(UNVERIFIED_AGENT_MODAL);
    }
  }, [isAgent, isListingAgent, isUnverified]);

  const handleNavigateToBookAppointment = useCallback(() => {
    history.push(`/listing/${listing.id}/appointments/book`);
  }, [history, listing.id]);

  const handleBookAppointment = useCallback(() => {
    if (
      auth.org &&
      auth.org.features.includes('APPOINTMENT') &&
      !(isAgent && !isListingAgent && isUnverified)
    ) {
      if (hasFeature('PRE_BOOKING') && preBookingInformation) {
        Modal.confirm({
          icon: null,
          title: 'Important Information',
          content: `${preBookingInformation}`,
          okText: 'Continue to booking',
          cancelText: 'Cancel',
          closable: true,
          onOk() {
            handleNavigateToBookAppointment();
          },
        });
        return;
      }

      handleNavigateToBookAppointment();
      return;
    }

    bookWarningModal();
  }, [
    auth.org,
    bookWarningModal,
    handleNavigateToBookAppointment,
    isAgent,
    isListingAgent,
    isUnverified,
    preBookingInformation,
  ]);

  useEffect(() => {
    const fetchUserFavorites = async () => {
      const fetchedUserFavorites = await FavoriteController.list();
      setUserFavorites(new Set(fetchedUserFavorites));
    };

    fetchUserFavorites();
  }, []);

  const toggleFavorite = useCallback(
    async (listingId: string) => {
      const result = await FavoriteController.toggle(listingId);
      if (result.exists) {
        userFavorites.delete(listingId);
        message.success('Removed from favorites!');
      } else {
        userFavorites.add(listingId);
        message.success('Added to favorites!');
      }
      setUserFavorites(new Set(userFavorites));
    },
    [userFavorites],
  );

  return (
    <StyledButtonContainer className="pull-right">
      {showRegisterOfferBtn && (
        <RegisterOfferButton
          isDisabledBtns={isDisabledBtns}
          listing={listing}
          isAgent={isAgent}
          isListingAgent={isListingAgent}
          isUnverified={isUnverified}
          org={auth.org}
          handleBookModal={bookWarningModal}
        />
      )}
      {!showAddToTour && showBookShowingBtn && (
        <BookShowingButton
          isAutoConfirm={isAutoConfirm}
          isDisabledBtns={isDisabledBtns}
          listing={listing}
          isAgent={isAgent}
          isListingAgent={isListingAgent}
          isUnverified={isUnverified}
          org={auth.org}
          handleBookModal={
            featureEnabled('preBookingNotificationFeature')
              ? handleBookAppointment
              : bookWarningModal
          }
        />
      )}
      {/* showAddToTour already includes a check for showBookShowingBtn */}
      {showAddToTour && (
        <DropdownBookingActions
          isAutoConfirm={isAutoConfirm}
          isDisabledBtns={isDisabledBtns}
          listing={listing}
          isAgent={isAgent}
          isListingAgent={isListingAgent}
          isUnverified={isUnverified}
          org={auth.org}
          handleBookModal={
            featureEnabled('preBookingNotificationFeature')
              ? handleBookAppointment
              : bookWarningModal
          }
          preBookingInformation={preBookingInformation}
        />
      )}
      {showQuickListingActionButtons && (
        <DropdownQuickListingActions
          isDisabledBtns={isDisabledBtns}
          isFavorite={isFavorite}
          isCoopAgent={isCoopAgent}
          listing={listing}
          toggleFavorite={toggleFavorite}
        />
      )}
    </StyledButtonContainer>
  );
};

const StyledButtonContainer = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  font-size: 14px;
  gap: 12px;
  justify-content: right;
  margin-right: 0.3em;
  flex-wrap: wrap;
  margin-left: 7px;
`;

export default (props: ListingActionButtonsType) => (
  <HashRouter>
    <Route render={() => <ListingActionButtons {...props} />} />
  </HashRouter>
);
